import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faIndustry,
    faBox,
    faCalendarAlt,
    faSearch,
    faPlus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faExclamationTriangle,
    faBarcode,
    faTag,
    faRupeeSign,
    faBuilding,
    faChartLine,
    faHistory,
    faFileInvoice,
    faClipboardList,
    faUser,
    faClock,
    faPrint,
    faDownload,
    faEye,
    faEdit,
    faFilter,
    faTimesCircle,
    faChevronLeft,
    faChevronRight,
    faWarehouse,
    faTachometerAlt,
    faChartBar,
    faChartPie,
    faShoppingCart,
    faPhone,
    faMapMarkerAlt,
    faCalculator,
    faBoxes,
    faReceipt,
    faQrcode,
    faStore
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createUsedProductsTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_used_products_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_used_products_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        usedProducts: 'Used Products',
        rawMaterialsConsumption: 'Raw Materials Consumption',
        newUsage: 'New Usage',
        usageHistory: 'Usage History',
        dashboard: 'Dashboard',
        selectProduct: 'Select Product',
        searchProduct: 'Search product...',
        productName: 'Product',
        batchNumber: 'Batch',
        barcode: 'Barcode',
        availableStock: 'Available Stock',
        quantityToUse: 'Quantity to Use',
        unit: 'Unit',
        costPerUnit: 'Cost per Unit',
        totalCost: 'Total Cost',
        referenceNo: 'Reference No',
        productionOrderNo: 'Production Order No',
        jobNo: 'Job No',
        department: 'Department',
        purpose: 'Purpose',
        remarks: 'Remarks',
        usedBy: 'Used By',
        date: 'Date',
        usageDate: 'Usage Date',
        confirmUsage: 'Confirm Usage',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        productRequired: 'Please select a product',
        quantityRequired: 'Quantity is required',
        usageRecorded: 'Raw material usage recorded successfully',
        stockUpdated: 'Stock updated successfully',
        noProductsFound: 'No raw materials found',
        outOfStock: 'Out of Stock',
        lowStock: 'Low Stock',
        totalUsage: 'Total Usage',
        totalQuantity: 'Total Quantity',
        totalCostValue: 'Total Cost',
        todayUsage: "Today's Usage",
        thisMonthUsage: 'This Month Usage',
        topUsedProducts: 'Top Used Products',
        usageCount: 'Usage Count',
        departmentUsage: 'Department Usage',
        viewDetails: 'View Details',
        deleteRecord: 'Delete Record',
        areYouSure: 'Are you sure?',
        deleteConfirmation: 'This action cannot be undone',
        cancel: 'Cancel',
        delete: 'Delete',
        printReport: 'Print Report',
        exportCSV: 'Export CSV',
        filters: 'Filters',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        dateFrom: 'Date From',
        dateTo: 'Date To',
        allDepartments: 'All Departments',
        production: 'Production',
        cutting: 'Cutting',
        stitching: 'Stitching',
        finishing: 'Finishing',
        quality: 'Quality Control',
        other: 'Other',
        items: 'Items',
        addItem: 'Add Item',
        remove: 'Remove',
        subtotal: 'Subtotal',
        grandTotal: 'Grand Total',
        calculations: 'Calculations',
        usageDetails: 'Usage Details',
        piece: 'Piece',
        box: 'Box',
        packet: 'Packet',
        kg: 'Kilogram',
        gram: 'Gram',
        liter: 'Liter',
        ml: 'Milliliter',
        dozen: 'Dozen',
        selectUnit: 'Select unit'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const usedProductsTranslator = createUsedProductsTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div className={`toast-notification ${toast.type}`} style={{ opacity }}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div className="toast-progress-bar" style={{ width: `${toast.progress}%` }}></div>
                <div className="toast-countdown">{toast.timeLeft}s</div>
            </div>
        </div>
    );
};

// ==============================================
// Items Table Component for Used Products
// ==============================================
const UsedItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    const units = [
        { value: 'piece', label: translations?.piece || 'Piece' },
        { value: 'box', label: translations?.box || 'Box' },
        { value: 'packet', label: translations?.packet || 'Packet' },
        { value: 'kg', label: translations?.kg || 'Kilogram' },
        { value: 'gram', label: translations?.gram || 'Gram' },
        { value: 'liter', label: translations?.liter || 'Liter' },
        { value: 'ml', label: translations?.ml || 'Milliliter' },
        { value: 'dozen', label: translations?.dozen || 'Dozen' }
    ];

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                        {translations?.items || 'Items'}
                    </h4>
                    <button
                        type="button"
                        className="btn-add-item-mobile"
                        onClick={onAdd}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => (
                        <div key={index} className="item-card-mobile">
                            <div className="item-card-header">
                                <span className="item-number">
                                    <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                    Item {index + 1}
                                </span>
                                <button
                                    type="button"
                                    className="item-remove-btn-mobile"
                                    onClick={() => onRemove(index)}
                                    disabled={disabled || items.length === 1}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>

                            <div className="item-card-content">
                                <div className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`}>
                                    <label>
                                        <FontAwesomeIcon icon={faBox} className="field-icon" />
                                        {translations?.productName || 'Product'}
                                    </label>
                                    <div className="product-select-info">
                                        {item.product_name ? (
                                            <>
                                                <div className="selected-product-name">{item.product_name}</div>
                                                {item.batch_no && (
                                                    <div className="batch-info">Batch: {item.batch_no}</div>
                                                )}
                                                <div className="stock-info">
                                                    Stock: {parseFloat(item.available_stock).toFixed(2)} {item.unit}
                                                </div>
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className="select-product-btn-small"
                                                onClick={() => onItemChange(index, 'showProductModal', true)}
                                            >
                                                <FontAwesomeIcon icon={faSearch} />
                                                Select Product
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="mobile-row">
                                    <div className={`mobile-field half ${errors[`unit_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.unit || 'Unit'} *</label>
                                        <select
                                            value={item.unit}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled || !item.product_id}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.quantityToUse || 'Qty'} *</label>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_stock}
                                            placeholder="0.000"
                                            disabled={disabled || !item.product_id}
                                        />
                                    </div>
                                </div>

                                <div className="mobile-row">
                                    <div className="mobile-field half">
                                        <label>{translations?.costPerUnit || 'Cost/Unit'}</label>
                                        <input
                                            type="number"
                                            value={item.cost_price}
                                            readOnly
                                            className="readonly-input"
                                        />
                                    </div>

                                    <div className="mobile-field half">
                                        <label>{translations?.totalCost || 'Total'}</label>
                                        <input
                                            type="text"
                                            value={calculateTotal(item.cost_price, item.quantity)}
                                            readOnly
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </div>

                                {errors[`quantity_${index}`] && (
                                    <div className="error-text">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors[`quantity_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {translations?.items || 'Items'}
                </h4>
                <button
                    type="button"
                    className="btn-add-item-table"
                    onClick={onAdd}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper" ref={tableRef}>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="product-col">
                                {translations?.productName || 'Product'} <span className="required">*</span>
                            </th>
                            <th className="batch-col">
                                {translations?.batchNumber || 'Batch'}
                            </th>
                            <th className="stock-col">
                                {translations?.availableStock || 'Stock'}
                            </th>
                            <th className="unit-col">
                                {translations?.unit || 'Unit'} <span className="required">*</span>
                            </th>
                            <th className="qty-col">
                                {translations?.quantityToUse || 'Qty'} <span className="required">*</span>
                            </th>
                            <th className="cost-col">
                                {translations?.costPerUnit || 'Cost/Unit'}
                            </th>
                            <th className="total-col">
                                {translations?.totalCost || 'Total'}
                            </th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index} className={errors[`quantity_${index}`] ? 'error-row' : ''}>
                                <td>
                                    <div className="table-field">
                                        {item.product_name ? (
                                            <div className="selected-product-info">
                                                <strong>{item.product_name}</strong>
                                                {item.batch_no && (
                                                    <small className="batch-display">Batch: {item.batch_no}</small>
                                                )}
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="select-product-table-btn"
                                                onClick={() => onItemChange(index, 'showProductModal', true)}
                                            >
                                                <FontAwesomeIcon icon={faSearch} />
                                                {translations?.selectProduct || 'Select Product'}
                                            </button>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.batch_no || ''}
                                            readOnly
                                            className="readonly-input"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <span className={`stock-display ${parseFloat(item.available_stock) <= (item.quantity_alert || 5) ? 'low-stock' : ''}`}>
                                            {parseFloat(item.available_stock).toFixed(2)} {item.unit}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <select
                                            value={item.unit}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled || !item.product_id}
                                            className={errors[`unit_${index}`] ? 'error' : ''}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_stock}
                                            placeholder="0.000"
                                            disabled={disabled || !item.product_id}
                                            className={errors[`quantity_${index}`] ? 'error' : ''}
                                        />
                                        {errors[`quantity_${index}`] && (
                                            <div className="table-error">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`quantity_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={parseFloat(item.cost_price).toFixed(2)}
                                            readOnly
                                            className="readonly-input"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={(parseFloat(item.cost_price) * parseFloat(item.quantity || 0)).toFixed(2)}
                                            readOnly
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="table-remove-btn"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || items.length === 1}
                                        title={translations?.remove || 'Remove'}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Product Selection Modal
// ==============================================
const ProductSelectionModal = ({ show, products, onSelect, onClose, translations, loading, itemIndex }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);

    useEffect(() => {
        if (products) {
            if (searchTerm) {
                setFilteredProducts(products.filter(p => 
                    p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (p.batch_no && p.batch_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
                ));
            } else {
                setFilteredProducts(products);
            }
        }
    }, [products, searchTerm]);

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content product-selection-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        <FontAwesomeIcon icon={faBox} />
                        {translations?.selectProduct || 'Select Product'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="product-search-input">
                        <FontAwesomeIcon icon={faSearch} />
                        <input
                            type="text"
                            placeholder={translations?.searchProduct || 'Search product...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <div className="products-list">
                        {loading ? (
                            <div className="loading-state">
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{translations?.loading || 'Loading...'}</span>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="empty-state">
                                <FontAwesomeIcon icon={faBox} />
                                <span>{translations?.noProductsFound || 'No raw materials found'}</span>
                            </div>
                        ) : (
                            filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    className={`product-item ${product.quantity <= 0 ? 'out-of-stock' : ''}`}
                                    onClick={() => product.quantity > 0 && onSelect(product, itemIndex)}
                                >
                                    <div className="product-info">
                                        <div className="product-name">{product.product_name}</div>
                                        <div className="product-details">
                                            {product.batch_no && (
                                                <span className="batch-no">
                                                    <FontAwesomeIcon icon={faTag} size="xs" />
                                                    {product.batch_no}
                                                </span>
                                            )}
                                            {product.barcode && (
                                                <span className="barcode">
                                                    <FontAwesomeIcon icon={faBarcode} size="xs" />
                                                    {product.barcode}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="product-stock-info">
                                        <div className={`stock-quantity ${product.quantity <= product.quantity_alert ? 'low-stock' : ''}`}>
                                            {translations?.availableStock || 'Stock'}: {parseFloat(product.quantity).toFixed(3)} {product.unit}
                                        </div>
                                        <div className="cost-info">
                                            <FontAwesomeIcon icon={faRupeeSign} size="xs" />
                                            {parseFloat(product.cost_price).toFixed(2)} / {product.unit}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main UsedProducts Component
// ==============================================
const UsedProducts = () => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    // Form state with multiple items
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        used_date: new Date().toISOString().slice(0, 16),
        reference_no: '',
        production_order_no: '',
        job_no: '',
        department: '',
        purpose: '',
        remarks: '',
        items: []
    });

    // Data states
    const [rawProducts, setRawProducts] = useState([]);
    const [usedRecords, setUsedRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [usageSummary, setUsageSummary] = useState(null);

    // UI states
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showProductModal, setShowProductModal] = useState(false);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [errors, setErrors] = useState({});
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        department: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0
    });

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);

        setToast({
            show: true,
            type,
            message,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await usedProductsTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        usedProductsTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            usedProductsTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Update shop info
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
            setShopId(currentShop.shop_id);
        }
        loadBusinessInfo();
    }, [currentShop, loadBusinessInfo]);

    // Load raw products
    const loadRawProducts = useCallback(async () => {
        if (!user?.username || !shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_raw_products',
                    username: user.username,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setRawProducts(data.data);
            }
        } catch (error) {
            console.error('Error loading raw products:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.username, shopId]);

    // Load used records
    const loadUsedRecords = useCallback(async () => {
        if (!user?.username || !shopId) return;

        setLoading(true);
        try {
            const payload = {
                action: 'get_used_records',
                username: user.username,
                shop_id: shopId,
                page: pagination.page,
                limit: pagination.limit
            };

            if (filters.date_from) payload.filters = { ...payload.filters, date_from: filters.date_from };
            if (filters.date_to) payload.filters = { ...payload.filters, date_to: filters.date_to };
            if (filters.department) payload.filters = { ...payload.filters, department: filters.department };

            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                setUsedRecords(data.data);
                setPagination(prev => ({ ...prev, total: data.total }));
                setSummary(data.summary);
            }
        } catch (error) {
            console.error('Error loading used records:', error);
        } finally {
            setLoading(false);
        }
    }, [user?.username, shopId, pagination.page, pagination.limit, filters]);

    // Load usage summary
    const loadUsageSummary = useCallback(async () => {
        if (!user?.username || !shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_usage_summary',
                    username: user.username,
                    shop_id: shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setUsageSummary(data.data);
            }
        } catch (error) {
            console.error('Error loading usage summary:', error);
        }
    }, [user?.username, shopId]);

    // Initial load
    useEffect(() => {
        if (shopId && user?.username) {
            loadRawProducts();
            loadUsedRecords();
            loadUsageSummary();
        }
    }, [shopId, user?.username, loadRawProducts, loadUsedRecords, loadUsageSummary]);

    // Add a new empty item
    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_id: '',
                product_name: '',
                batch_no: '',
                barcode: '',
                unit: '',
                quantity: '',
                cost_price: 0,
                available_stock: 0,
                quantity_alert: 5,
                showProductModal: false
            }]
        }));
    };

    // Remove an item
    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({
                ...prev,
                items: updatedItems
            }));

            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`quantity_${index}`];
                return newErrors;
            });
        }
    };

    // Handle item field changes
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        
        if (field === 'showProductModal') {
            setCurrentItemIndex(index);
            setShowProductModal(value);
            return;
        }
        
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        if (field === 'quantity') {
            const qty = parseFloat(value) || 0;
            const maxQty = parseFloat(updatedItems[index].available_stock) || 0;

            if (qty > maxQty) {
                setErrors(prev => ({
                    ...prev,
                    [`quantity_${index}`]: translations?.quantityExceedsStock || 'Quantity exceeds available stock'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`quantity_${index}`];
                    return newErrors;
                });
            }
        }

        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));
    };

    // Handle product selection for a specific item
    const handleSelectProduct = (product, itemIndex) => {
        const updatedItems = [...formData.items];
        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            product_id: product.id,
            product_name: product.product_name,
            batch_no: product.batch_no || '',
            barcode: product.barcode || '',
            unit: product.unit,
            cost_price: parseFloat(product.cost_price) || 0,
            available_stock: parseFloat(product.quantity) || 0,
            quantity_alert: product.quantity_alert || 5,
            quantity: ''
        };
        
        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));
        
        setShowProductModal(false);
        setCurrentItemIndex(null);
    };

    // Calculate total cost for all items
    const calculateTotalCost = () => {
        let total = 0;
        formData.items.forEach(item => {
            const quantity = parseFloat(item.quantity) || 0;
            const costPrice = parseFloat(item.cost_price) || 0;
            total += quantity * costPrice;
        });
        return total.toFixed(2);
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        
        if (formData.items.length === 0) {
            newErrors.items = 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                if (!item.product_id) {
                    newErrors[`product_${index}`] = 'Please select a product';
                }
                
                const quantity = parseFloat(item.quantity);
                if (!item.quantity || isNaN(quantity) || quantity <= 0) {
                    newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                } else if (quantity > parseFloat(item.available_stock)) {
                    newErrors[`quantity_${index}`] = translations?.quantityExceedsStock || 'Quantity exceeds available stock';
                }
            });
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit usage for all items
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!shopId) {
            showToast('error', 'No shop selected. Please select a shop first.');
            return;
        }
        
        if (!validateForm()) return;
        
        setSaving(true);
        
        try {
            // Submit each item as a separate usage record
            const results = [];
            for (const item of formData.items) {
                const payload = {
                    action: 'add_used_product',
                    product_id: item.product_id,
                    product_name: item.product_name,
                    batch_no: item.batch_no,
                    quantity_used: parseFloat(item.quantity),
                    used_by: user?.username,
                    shop_id: shopId,
                    date: formData.date,
                    used_date: formData.used_date,
                    reference_no: formData.reference_no,
                    production_order_no: formData.production_order_no,
                    job_no: formData.job_no,
                    department: formData.department,
                    purpose: formData.purpose,
                    remarks: formData.remarks
                };
                
                const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                
                const data = await response.json();
                results.push(data);
                
                if (data.status !== 'success') {
                    throw new Error(data.message || `Failed to record usage for ${item.product_name}`);
                }
            }
            
            // Check if all submissions were successful
            const allSuccess = results.every(r => r.status === 'success');
            
            if (allSuccess) {
                showToast('success', `${results.length} item(s) usage recorded successfully`);
                
                // Reset form
                setFormData({
                    date: new Date().toISOString().split('T')[0],
                    used_date: new Date().toISOString().slice(0, 16),
                    reference_no: '',
                    production_order_no: '',
                    job_no: '',
                    department: '',
                    purpose: '',
                    remarks: '',
                    items: []
                });
                
                // Refresh data
                loadRawProducts();
                loadUsedRecords();
                loadUsageSummary();
            } else {
                showToast('error', 'Some items failed to record');
            }
        } catch (error) {
            showToast('error', error.message || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    // Handle clear form
    const handleClear = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            used_date: new Date().toISOString().slice(0, 16),
            reference_no: '',
            production_order_no: '',
            job_no: '',
            department: '',
            purpose: '',
            remarks: '',
            items: []
        });
        setErrors({});
    };

    // Handle delete record
    const handleDelete = async (id) => {
        if (!window.confirm(translations?.deleteConfirmation || 'Are you sure you want to delete this record? This action cannot be undone.')) {
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_used_record',
                    id: id,
                    username: user?.username,
                    shop_id: shopId
                })
            });
            
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', 'Record deleted and stock restored');
                loadUsedRecords();
                loadRawProducts();
                loadUsageSummary();
            } else {
                showToast('error', data.message || 'Failed to delete record');
            }
        } catch (error) {
            showToast('error', error.message || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN');
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const totalCost = calculateTotalCost();

    const departments = [
        { value: 'production', label: translations?.production || 'Production' },
        { value: 'cutting', label: translations?.cutting || 'Cutting' },
        { value: 'stitching', label: translations?.stitching || 'Stitching' },
        { value: 'finishing', label: translations?.finishing || 'Finishing' },
        { value: 'quality', label: translations?.quality || 'Quality Control' },
        { value: 'other', label: translations?.other || 'Other' }
    ];

    // Business Info Component
    const BusinessInfo = ({ businessInfo, translations }) => {
        if (!businessInfo) return null;

        return (
            <div className="business-info-card" style={{
                background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                borderRadius: '12px',
                padding: '15px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
            }}>
                {businessInfo.bussinesslogo && (
                    <img
                        src={businessInfo.bussinesslogo}
                        alt="Logo"
                        style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'contain',
                            borderRadius: '8px'
                        }}
                    />
                )}
                <div className="business-details">
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                        {businessInfo.businessname}
                    </div>
                    {businessInfo.businessaddress && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                            {businessInfo.businessaddress}
                        </div>
                    )}
                    {businessInfo.phonenumber && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                            {businessInfo.phonenumber}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Product Selection Modal */}
            <ProductSelectionModal
                show={showProductModal}
                products={rawProducts}
                onSelect={handleSelectProduct}
                onClose={() => {
                    setShowProductModal(false);
                    setCurrentItemIndex(null);
                }}
                translations={translations}
                loading={loading}
                itemIndex={currentItemIndex}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faIndustry} />
                    </div>
                    <div className="header-text">
                        <h1>{translations?.usedProducts || 'Used Products'}</h1>
                        <p>{translations?.rawMaterialsConsumption || 'Raw Materials Consumption'}</p>
                    </div>
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Summary Cards */}
            {usageSummary && (
                <div className="summary-cards" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px',
                    marginBottom: '25px'
                }}>
                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                    }}>
                        <div className="card-icon today" style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '12px',
                            background: '#e0e7ff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4f46e5'
                        }}>
                            <FontAwesomeIcon icon={faClock} size="lg" />
                        </div>
                        <div className="card-info">
                            <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#64748b' }}>{translations?.todayUsage || "Today's Usage"}</h4>
                            <div className="card-stats">
                                <div style={{ fontWeight: '600', fontSize: '18px' }}>{usageSummary.today.total_used} uses</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{usageSummary.today.total_quantity.toFixed(2)} units</div>
                                <div style={{ fontSize: '12px', color: '#4f46e5' }}>Rs.{usageSummary.today.total_cost.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="summary-card" style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '15px'
                    }}>
                        <div className="card-icon month" style={{
                            width: '50px',
                            height: '50px',
                            borderRadius: '12px',
                            background: '#d1fae5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#10b981'
                        }}>
                            <FontAwesomeIcon icon={faCalendarAlt} size="lg" />
                        </div>
                        <div className="card-info">
                            <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#64748b' }}>{translations?.thisMonthUsage || 'This Month Usage'}</h4>
                            <div className="card-stats">
                                <div style={{ fontWeight: '600', fontSize: '18px' }}>{usageSummary.this_month.total_used} uses</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{usageSummary.this_month.total_quantity.toFixed(2)} units</div>
                                <div style={{ fontSize: '12px', color: '#10b981' }}>Rs.{usageSummary.this_month.total_cost.toFixed(2)}</div>
                            </div>
                        </div>
                    </div>

                    {summary && (
                        <div className="summary-card" style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '20px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px'
                        }}>
                            <div className="card-icon total" style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '12px',
                                background: '#fed7aa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#f97316'
                            }}>
                                <FontAwesomeIcon icon={faChartLine} size="lg" />
                            </div>
                            <div className="card-info">
                                <h4 style={{ margin: '0 0 5px 0', fontSize: '14px', color: '#64748b' }}>{translations?.totalUsage || 'Total Usage'}</h4>
                                <div className="card-stats">
                                    <div style={{ fontWeight: '600', fontSize: '18px' }}>{summary.total_used_records} records</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{summary.total_quantity_used.toFixed(2)} units</div>
                                    <div style={{ fontSize: '12px', color: '#f97316' }}>Rs.{summary.total_cost.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Main Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Usage Information Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faFileInvoice} />
                            {translations?.usageDetails || 'Usage Details'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="date" className="input-label">
                                    {translations?.date || 'Date'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faClock} className="input-icon" />
                                <input
                                    type="date"
                                    id="used_date"
                                    name="used_date"
                                    value={formData.used_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, used_date: e.target.value }))}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="used_date" className="input-label">
                                    {translations?.usageDate || 'Usage Date'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="text"
                                    id="reference_no"
                                    name="reference_no"
                                    value={formData.reference_no}
                                    onChange={(e) => setFormData(prev => ({ ...prev, reference_no: e.target.value }))}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="reference_no" className="input-label">
                                    {translations?.referenceNo || 'Reference No'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faClipboardList} className="input-icon" />
                                <input
                                    type="text"
                                    id="production_order_no"
                                    name="production_order_no"
                                    value={formData.production_order_no}
                                    onChange={(e) => setFormData(prev => ({ ...prev, production_order_no: e.target.value }))}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="production_order_no" className="input-label">
                                    {translations?.productionOrderNo || 'Production Order No'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faTag} className="input-icon" />
                                <input
                                    type="text"
                                    id="job_no"
                                    name="job_no"
                                    value={formData.job_no}
                                    onChange={(e) => setFormData(prev => ({ ...prev, job_no: e.target.value }))}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="job_no" className="input-label">
                                    {translations?.jobNo || 'Job No'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faBuilding} className="input-icon" />
                                <select
                                    id="department"
                                    name="department"
                                    value={formData.department}
                                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                >
                                    <option value="">{translations?.allDepartments || 'Select Department'}</option>
                                    {departments.map(dept => (
                                        <option key={dept.value} value={dept.value}>{dept.label}</option>
                                    ))}
                                </select>
                                <label htmlFor="department" className="input-label">
                                    {translations?.department || 'Department'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faChartLine} className="input-icon" />
                                <input
                                    type="text"
                                    id="purpose"
                                    name="purpose"
                                    value={formData.purpose}
                                    onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="purpose" className="input-label">
                                    {translations?.purpose || 'Purpose'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern full-width">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                <textarea
                                    id="remarks"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                                    placeholder=" "
                                    disabled={saving}
                                    rows="3"
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                ></textarea>
                                <label htmlFor="remarks" className="input-label">
                                    {translations?.remarks || 'Remarks'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Items Section */}
                <div className="form-section">
                    <UsedItemsTable
                        items={formData.items}
                        onItemChange={handleItemChange}
                        onRemove={removeItem}
                        onAdd={addItem}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                    />
                    {errors.items && (
                        <div className="error-message-modern" style={{ marginTop: '10px', color: '#dc2626', fontSize: '12px' }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '5px' }} />
                            {errors.items}
                        </div>
                    )}
                </div>

                {/* Calculations Section */}
                {formData.items.length > 0 && (
                    <div className="form-section">
                        <div className="section-header">
                            <h3 className="section-title">
                                <FontAwesomeIcon icon={faCalculator} />
                                {translations?.calculations || 'Calculations'}
                            </h3>
                        </div>

                        <div className="calculations-grid">
                            <div className="calculation-row grand-total">
                                <span className="calculation-label">{translations?.totalCost || 'Total Cost'}:</span>
                                <span className="calculation-value highlight">Rs. {totalCost}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating || formData.items.length === 0}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{translations?.confirmUsage || 'Confirm Usage'}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Usage History Section */}
            <div className="form-section" style={{ marginTop: '30px' }}>
                <div className="section-header">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faHistory} />
                        {translations?.usageHistory || 'Usage History'}
                    </h3>
                    <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
                        <FontAwesomeIcon icon={faFilter} />
                        {translations?.filters || 'Filters'}
                    </button>
                </div>

                {/* Filters */}
                {showFilters && (
                    <div className="filters-panel" style={{
                        display: 'flex',
                        gap: '15px',
                        flexWrap: 'wrap',
                        padding: '15px',
                        background: '#f8fafc',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}>
                        <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.dateFrom || 'Date From'}</label>
                            <input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            />
                        </div>
                        <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.dateTo || 'Date To'}</label>
                            <input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            />
                        </div>
                        <div className="filter-group" style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#64748b' }}>{translations?.department || 'Department'}</label>
                            <select
                                value={filters.department}
                                onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                                style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                            >
                                <option value="">{translations?.allDepartments || 'All Departments'}</option>
                                {departments.map(dept => (
                                    <option key={dept.value} value={dept.value}>{dept.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-actions" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                            <button className="btn-apply" onClick={() => { setPagination(prev => ({ ...prev, page: 1 })); loadUsedRecords(); }} style={{
                                padding: '8px 16px',
                                background: '#4f46e5',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}>
                                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '5px' }} />
                                {translations?.applyFilters || 'Apply'}
                            </button>
                            <button className="btn-reset" onClick={() => {
                                setFilters({ date_from: '', date_to: '', department: '' });
                                setPagination(prev => ({ ...prev, page: 1 }));
                                loadUsedRecords();
                            }} style={{
                                padding: '8px 16px',
                                background: '#e2e8f0',
                                color: '#475569',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}>
                                <FontAwesomeIcon icon={faTimesCircle} style={{ marginRight: '5px' }} />
                                {translations?.resetFilters || 'Reset'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Records Table */}
                {loading ? (
                    <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: '#4f46e5' }} />
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                ) : usedRecords.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                        <FontAwesomeIcon icon={faHistory} style={{ fontSize: '48px', marginBottom: '10px' }} />
                        <p>No usage records found</p>
                    </div>
                ) : (
                    <>
                        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                            <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.date || 'Date'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.productName || 'Product'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.batchNumber || 'Batch'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.quantityToUse || 'Quantity'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.totalCost || 'Total Cost'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.referenceNo || 'Ref No'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.department || 'Dept'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.usedBy || 'Used By'}</th>
                                        <th style={{ padding: '12px', textAlign: 'left' }}>{translations?.actions || 'Actions'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usedRecords.map(record => (
                                        <tr key={record.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '12px' }}>{formatDate(record.date)}</td>
                                            <td style={{ padding: '12px' }}>
                                                <strong>{record.product_name}</strong>
                                                {record.production_order_no && (
                                                    <small style={{ display: 'block', fontSize: '11px', color: '#64748b' }}>PO: {record.production_order_no}</small>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px' }}>{record.batch_no || '-'}</td>
                                            <td style={{ padding: '12px' }}>{parseFloat(record.quantity_used).toFixed(3)} {record.unit}</td>
                                            <td style={{ padding: '12px' }}>Rs.{parseFloat(record.total_cost).toFixed(2)}</td>
                                            <td style={{ padding: '12px' }}>{record.reference_no || '-'}</td>
                                            <td style={{ padding: '12px' }}>
                                                {record.department && (
                                                    <span style={{
                                                        padding: '2px 8px',
                                                        borderRadius: '12px',
                                                        background: '#e0e7ff',
                                                        fontSize: '11px',
                                                        color: '#4f46e5'
                                                    }}>
                                                        {translations?.[record.department] || record.department}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px' }}>{record.used_by}</td>
                                            <td style={{ padding: '12px' }}>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => handleDelete(record.id)}
                                                    title={translations?.deleteRecord || 'Delete Record'}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#dc2626',
                                                        cursor: 'pointer',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.total > pagination.limit && (
                            <div className="pagination" style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '10px',
                                marginTop: '20px',
                                alignItems: 'center'
                            }}>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    disabled={pagination.page === 1}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        background: 'white',
                                        cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                                        opacity: pagination.page === 1 ? 0.5 : 1
                                    }}
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                                <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                                <button
                                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        background: 'white',
                                        cursor: pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 'not-allowed' : 'pointer',
                                        opacity: pagination.page >= Math.ceil(pagination.total / pagination.limit) ? 0.5 : 1
                                    }}
                                >
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div className="loading-content" style={{
                        background: 'white',
                        padding: '30px',
                        borderRadius: '12px',
                        textAlign: 'center'
                    }}>
                        <div className="loading-spinner" style={{
                            width: '40px',
                            height: '40px',
                            border: '3px solid #e2e8f0',
                            borderTopColor: '#4f46e5',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 15px'
                        }}></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default UsedProducts;